/**
 * ハーマン・コミュニケーション負荷 → つながりスコア補正 (設計ドラフト実装)
 *
 * docs/gbpav74_ハーマン負荷_つながりスコア反映_設計.md を実装する。
 * 組織ダイヤモンド(A/B/C/D)の「組み合わさり方」から潜在的なコミュニケーション負荷を算出し、
 * つながり調整値への **補正係数(nudge)** と、丸める前の **PIシグナル** を返す。
 *
 * 設計の要点:
 *  - 掛け算をやめ 2×2マトリクス(分極×橋渡し)＋独立の脆さ減点で「有害な丸め」を排除。
 *  - 対角ペアを分けて保持 (A↔C=しくみ対ひと / B↔D=守る対変える)。
 *  - 最小Nゲート＋小N縮約。同質型は脆さで必ず減点 (最上位に来ない)。
 *  - 学術根拠レベル3(仮説)。閾値・nudge幅はパイロット検証項目。
 */
import type { AxisKey } from './gb74Report';

// ===== 閾値・係数 (パイロット検証項目。テストから参照) =====
export const MIN_N = 7; // これ未満は補正を適用しない (小Nノイズ)
export const SHRINK_K = 6; // 小N縮約の強さ (N/(N+K) で中立へ寄せる)
export const POLAR_HI = 0.3; // 分極「高」の閾値 (0〜1)
export const BRIDGE_HI = 0.25; // 橋渡し在庫「厚い」の閾値 (通訳人材25%以上)
export const FRAGILITY_HI = 0.5; // 脆さ「高」の閾値
export const OMNI_SPREAD = 4; // 個人スプレッド(max-min,0〜12)がこれ未満=全方型(橋渡し)
export const NEAR_TOP2 = 3; // 上位2軸の差がこれ以下なら第2軸も効く
// nudge幅 (つながり調整値を何%下げるか)
export const NUDGE = { large: 0.1, medium: 0.06, small: 0.03 } as const;
export const NUDGE_MAX = 0.15;

/** 隣接軸ペア (通訳になれる=橋渡し)。A-C, B-D は対角(非隣接) */
const ADJACENT = new Set(['AB', 'BA', 'BC', 'CB', 'CD', 'DC', 'DA', 'AD']);

export type LoadCell = 'X' | 'Yprime' | 'Y' | 'ok';

export interface HerrmannLoadMember {
  axis: Record<AxisKey, number>; // 0〜12
  /** 緩衝力の素 = 配慮性・感情制御の平均 (1.0〜5.0)。無い場合 null */
  buffer?: number | null;
}

export interface HerrmannLoadResult {
  applicable: boolean; // N>=MIN_N で補正適用可
  n: number;
  primaryRatios: Record<AxisKey, number>; // 縮約後のプライマリー比率
  polarAC: number; // 対角分極 A↔C (0〜1)
  polarBD: number; // 対角分極 B↔D (0〜1)
  polarization: number; // max(polarAC, polarBD)
  worstDiagonal: 'AC' | 'BD' | null;
  balanceAC: number; // 勢力バランス (1=二大勢力 / 小=少数派孤立)
  balanceBD: number;
  bridge: number; // 橋渡し在庫 (0〜1)
  buffer: number; // 緩衝力 (0〜1)
  fragility: number; // 脆さ=認知バランスの偏り (0〜1)
  cell: LoadCell; // 2×2マトリクスのセル
  fragilityPenalty: number;
  nudge: number; // 最終補正 (0〜NUDGE_MAX)。つながり調整値×(1-nudge)
  /** PI(アクション)へ流す見出し・テーマ */
  pi: { headline: string; theme: string; note: string };
}

const AXES: AxisKey[] = ['A', 'B', 'C', 'D'];
const DIAG_LABEL: Record<'AC' | 'BD', string> = {
  AC: 'しくみ ↔ ひと（A↔C）',
  BD: '守る ↔ 変える（B↔D）',
};
const DIAG_THEME: Record<'AC' | 'BD', string> = {
  AC: '役割・対話の設計',
  BD: '変革の合意形成',
};

/** 1メンバーのプライマリー軸 (最大の軸。同点は A,B,C,D 順で先頭) */
function primaryAxis(axis: Record<AxisKey, number>): AxisKey {
  return AXES.reduce((best, k) => (axis[k] > axis[best] ? k : best), 'A' as AxisKey);
}

/** 1メンバーが橋渡し人材か (隣接2軸近接 or 全方型) */
function isBridge(axis: Record<AxisKey, number>): boolean {
  const sorted = [...AXES].sort((a, b) => axis[b] - axis[a]);
  const spread = axis[sorted[0]] - axis[sorted[3]];
  if (spread < OMNI_SPREAD) return true; // 全方型(バランス)
  const top2 = sorted[0] + sorted[1];
  const adjacent = ADJACENT.has(top2);
  const close = axis[sorted[0]] - axis[sorted[1]] <= NEAR_TOP2;
  return adjacent && close; // 隣接2軸が拮抗 = 通訳になれる
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * メンバー配列からハーマン負荷を算出する。
 */
export function computeHerrmannLoad(members: HerrmannLoadMember[]): HerrmannLoadResult {
  const n = members.length;
  const empty: HerrmannLoadResult = {
    applicable: false,
    n,
    primaryRatios: { A: 0, B: 0, C: 0, D: 0 },
    polarAC: 0,
    polarBD: 0,
    polarization: 0,
    worstDiagonal: null,
    balanceAC: 0,
    balanceBD: 0,
    bridge: 0,
    buffer: 0.5,
    fragility: 0,
    cell: 'ok',
    fragilityPenalty: 0,
    nudge: 0,
    pi: { headline: '', theme: '', note: '' },
  };
  if (n === 0) return empty;

  // ---- プライマリー比率 ----
  const count: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  for (const m of members) count[primaryAxis(m.axis)] += 1;
  const rawP: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  AXES.forEach((k) => (rawP[k] = count[k] / n));

  // ---- 小N縮約: 比率を中立(0.25)へ寄せる ----
  const w = n / (n + SHRINK_K);
  const p: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  AXES.forEach((k) => (p[k] = 0.25 + w * (rawP[k] - 0.25)));

  // ---- 対角分極 (ペア別) ----
  const polarAC = clamp01(4 * p.A * p.C);
  const polarBD = clamp01(4 * p.B * p.D);
  const polarization = Math.max(polarAC, polarBD);
  const worstDiagonal: 'AC' | 'BD' | null =
    polarization <= 0 ? null : polarAC >= polarBD ? 'AC' : 'BD';

  // ---- 勢力バランス (副次シグナル) ----
  const bal = (x: number, y: number) => (Math.max(x, y) === 0 ? 0 : Math.min(x, y) / Math.max(x, y));
  const balanceAC = bal(p.A, p.C);
  const balanceBD = bal(p.B, p.D);

  // ---- 橋渡し在庫 ----
  const bridgeCount = members.filter((m) => isBridge(m.axis)).length;
  const bridge = bridgeCount / n;

  // ---- 緩衝力 (配慮性・感情制御) 1〜5 → 0〜1 ----
  const buffers = members.map((m) => m.buffer).filter((v): v is number => v != null);
  const bufferAvg = buffers.length ? buffers.reduce((a, b) => a + b, 0) / buffers.length : 3.0;
  const buffer = clamp01((bufferAvg - 1) / 4);

  // ---- 脆さ = 認知バランスの偏り (Simpson 有効タイプ数) ----
  const sumSq = AXES.reduce((s, k) => s + p[k] * p[k], 0);
  const effTypes = sumSq > 0 ? 1 / sumSq : 4;
  const fragility = clamp01(1 - (effTypes - 1) / 3);

  // ---- 2×2マトリクス ----
  const polHi = polarization >= POLAR_HI;
  const bridgeThick = bridge >= BRIDGE_HI;
  let cell: LoadCell;
  let cellNudge: number;
  if (polHi && bridgeThick) {
    cell = 'X'; // 分極高＋橋渡し厚 → 維持
    cellNudge = 0;
  } else if (polHi && !bridgeThick) {
    cell = 'Yprime'; // 分極高＋橋渡し薄 → 大きく下げ「橋を架けろ」
    cellNudge = NUDGE.large;
  } else if (!polHi && !bridgeThick) {
    cell = 'Y'; // 分極低＋橋渡し薄 → 小さく下げ「予兆を見よ」
    cellNudge = NUDGE.small;
  } else {
    cell = 'ok'; // 分極低＋橋渡し厚 → 据え置き
    cellNudge = 0;
  }
  // 緩衝力で Yprime を一段緩める (対立を協働に変える力)
  if (cell === 'Yprime' && buffer >= 0.6) cellNudge = NUDGE.medium;

  // ---- 脆さ減点 (独立。同質型を最上位にしない) ----
  const fragilityPenalty = fragility >= FRAGILITY_HI ? (fragility >= 0.75 ? NUDGE.medium : NUDGE.small) : 0;

  const nudge = Math.min(NUDGE_MAX, cellNudge + fragilityPenalty);

  // ---- PI (アクション) ----
  const diagLabel = worstDiagonal ? DIAG_LABEL[worstDiagonal] : '';
  const theme = worstDiagonal ? DIAG_THEME[worstDiagonal] : '現状維持';
  const headlineByCell: Record<LoadCell, string> = {
    X: '機能している通訳役を維持する',
    Yprime: '亀裂が広がる前に通訳役(橋渡し)を作る',
    Y: '分極の予兆を見て、通訳役を育てる',
    ok: '多様性が橋渡しされ機能している',
  };
  let headline = headlineByCell[cell];
  const notes: string[] = [];
  if (worstDiagonal && polHi) notes.push(`${diagLabel} の噛み合わなさが相対的に高い状態です。`);
  if (cell === 'X') notes.push('通訳役(橋渡し人材)がいて機能しています。この状態を維持してください。');
  if (cell === 'Yprime') notes.push('橋渡し人材が薄く、亀裂が固定しやすい構造です。通訳役の育成が最優先です。');
  if (cell === 'Y') notes.push('分極は浅めですが橋渡しも薄いため、予兆のうちに手を打つのが有効です。');
  if (fragilityPenalty > 0) {
    headline += '／認知の死角に注意';
    notes.push('特定の思考タイプに偏り(同質化)、対角の視点が組織に不足しています(脆さ)。');
  }

  return {
    applicable: n >= MIN_N,
    n,
    primaryRatios: p,
    polarAC,
    polarBD,
    polarization,
    worstDiagonal,
    balanceAC,
    balanceBD,
    bridge,
    buffer,
    fragility,
    cell,
    fragilityPenalty,
    nudge,
    pi: { headline, theme, note: notes.join(' ') },
  };
}

// ===== 表示用 (§01 に出すサマリ) =====
export interface HerrmannLoadDisplay {
  cell: LoadCell;
  cellLabel: string; // 「分極高×橋渡し薄」等
  worstDiagonalLabel: string; // 「守る↔変える(B↔D)」等
  polarizationPct: number; // 0〜100
  bridgePct: number;
  fragilityPct: number;
  bufferPct: number;
  nudgePct: number; // つながりを何%下げたか
  connBefore: number; // 補正前つながりスコア
  connAfter: number; // 補正後つながりスコア
  piHeadline: string;
  piTheme: string;
  note: string;
  applied: boolean; // 実際に補正を適用したか (N>=MIN_N)
}

const CELL_LABEL: Record<LoadCell, string> = {
  X: '分極 高 × 橋渡し 厚（維持）',
  Yprime: '分極 高 × 橋渡し 薄（橋を架けろ）',
  Y: '分極 低 × 橋渡し 薄（予兆を見よ）',
  ok: '分極 低 × 橋渡し 厚（良好）',
};

/** 算出結果 + 補正前後スコアから §01 表示用データを作る */
export function toLoadDisplay(
  load: HerrmannLoadResult,
  connBefore: number,
  connAfter: number,
  applied: boolean
): HerrmannLoadDisplay {
  return {
    cell: load.cell,
    cellLabel: CELL_LABEL[load.cell],
    worstDiagonalLabel: load.worstDiagonal ? DIAG_LABEL[load.worstDiagonal] : 'なし',
    polarizationPct: Math.round(load.polarization * 100),
    bridgePct: Math.round(load.bridge * 100),
    fragilityPct: Math.round(load.fragility * 100),
    bufferPct: Math.round(load.buffer * 100),
    nudgePct: Math.round(load.nudge * 100),
    connBefore,
    connAfter,
    piHeadline: load.pi.headline,
    piTheme: load.pi.theme,
    note: load.pi.note,
    applied,
  };
}
